// Dialog (DESIGN §3.7.2): zentriert, Breite dialog-s/m/l, Radius 20, `card`, `shadow-3`,
// Innenabstand 24; Titel `title-detail`, Text `body` `fg-secondary`, Aktionen rechtsbündig (mobil
// gestapelt, Primär unten). Hinein: Deckkraft + scale(.98 → 1) 200 ms.
// ConfirmDialog = Variante `alert` (Radix AlertDialog): kein Schließen per Scrim, Fokus startet bei
// destruktiven Aktionen auf „Cancel“.
import { useState, type ReactNode } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { AlertDialog as AlertPrimitive, Dialog as DialogPrimitive } from 'radix-ui';
import { Button } from './Button';
import { IconButton } from './IconButton';
import './_lib/motion.css';
import { LayerProvider } from './_lib/layer';
import { cn } from '../basis/kern';

export type DialogSize = 's' | 'm' | 'l';

const widthClass: Record<DialogSize, string> = {
  s: 'w-dialog-s',
  m: 'w-dialog-m',
  l: 'w-dialog-l',
};

export const scrimClass = 'bar-st-scrim fixed inset-0 z-(--bar-z-dialog) bg-scrim';

const panelClass = (size: DialogSize) =>
  cn(
    'bar-st-dialog fixed top-1/2 left-1/2 z-(--bar-z-dialog) -translate-x-1/2 -translate-y-1/2',
    'flex max-h-[calc(100dvh-32px)] max-w-[calc(100vw-32px)] flex-col rounded-2xl bg-card p-6 text-fg shadow-3',
    'focus-visible:outline-none',
    widthClass[size],
  );

export const dialogFooterClass =
  'mt-6 flex shrink-0 justify-end gap-3 max-sm:flex-col-reverse max-sm:[&>*]:w-full';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogContentProps {
  title: ReactNode;
  description?: ReactNode;
  size?: DialogSize;
  /** Aktionen rechts unten (Sekundär zuerst, Primär zuletzt). */
  footer?: ReactNode;
  /** ×-Knopf oben rechts (Standard an). */
  closable?: boolean;
  className?: string;
  children?: ReactNode;
}

export function DialogContent({
  title,
  description,
  size = 'm',
  footer,
  closable = true,
  className,
  children,
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={scrimClass} />
      <DialogPrimitive.Content
        className={cn(panelClass(size), className)}
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <LayerProvider z="var(--bar-z-dialog)">
          <div className="flex items-start gap-4">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="text-title-detail text-fg">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-2 text-body text-fg-secondary">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            {closable ? (
              <DialogPrimitive.Close asChild>
                <IconButton
                  label="Close"
                  icon={<XIcon />}
                  variant="ghost"
                  surface="card"
                  size="sm"
                  tooltip={false}
                  className="-mt-1 -mr-2"
                />
              </DialogPrimitive.Close>
            ) : null}
          </div>
          {children ? <div className="mt-4 min-h-0 overflow-y-auto">{children}</div> : null}
          {footer ? <div className={dialogFooterClass}>{footer}</div> : null}
        </LayerProvider>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export interface ConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Auslöser (optional, sonst kontrolliert über `open`). */
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger`: roter Bestätigungsknopf, Fokus startet auf „Cancel“. */
  tone?: 'default' | 'danger';
  /** Darf ein Promise liefern: Knopf zeigt `loading`, Dialog schließt erst nach Erfolg. */
  onConfirm: () => void | Promise<void>;
  size?: DialogSize;
  children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  size = 's',
  children,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = open ?? innerOpen;
  const setOpen = (next: boolean) => {
    if (busy) return;
    if (open === undefined) setInnerOpen(next);
    onOpenChange?.(next);
  };
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      setBusy(false);
      if (open === undefined) setInnerOpen(false);
      onOpenChange?.(false);
    } catch {
      // Fehler meldet der Aufrufer (Toast); der Dialog bleibt offen für „Retry“.
      setBusy(false);
    }
  };
  const danger = tone === 'danger';
  return (
    <AlertPrimitive.Root open={isOpen} onOpenChange={setOpen}>
      {trigger ? <AlertPrimitive.Trigger asChild>{trigger}</AlertPrimitive.Trigger> : null}
      <AlertPrimitive.Portal>
        <AlertPrimitive.Overlay className={scrimClass} />
        <AlertPrimitive.Content
          className={panelClass(size)}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <LayerProvider z="var(--bar-z-dialog)">
            <AlertPrimitive.Title className="text-title-detail text-fg">
              {title}
            </AlertPrimitive.Title>
            {description ? (
              <AlertPrimitive.Description className="mt-2 text-body text-fg-secondary">
                {description}
              </AlertPrimitive.Description>
            ) : null}
            {children ? <div className="mt-4">{children}</div> : null}
            <div className={dialogFooterClass}>
              <AlertPrimitive.Cancel asChild>
                <Button variant="secondary" surface="card" disabled={busy}>
                  {cancelLabel}
                </Button>
              </AlertPrimitive.Cancel>
              <Button
                variant={danger ? 'danger' : 'primary'}
                loading={busy}
                onClick={() => void confirm()}
              >
                {confirmLabel}
              </Button>
            </div>
          </LayerProvider>
        </AlertPrimitive.Content>
      </AlertPrimitive.Portal>
    </AlertPrimitive.Root>
  );
}
